import { apiClient } from './api-client';

export type ReceivingLotStatus = 'OPEN' | 'RECEIVING_COMPLETE' | 'CLOSED';

export interface ModelBatchSummary {
  id: string;
  equipmentModelId: string;
  equipmentModelName?: string | null;
  equipmentCount: number;
}

export interface ReceivingLot {
  id: string;
  lotNumber: string;
  status: ReceivingLotStatus;
  customerId: string;
  customerName: string;
  customerLocationId?: string | null;
  customerLocationName?: string | null;
  equipmentCount: number;
  receivedAt: string;
  receivedByUserId: string;
  notes?: string | null;
  modelBatches: ModelBatchSummary[];
  arrivalCount: number;
  openingArrivalAt: string;
  latestArrivalAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReceivingLotListItem {
  id: string;
  lotNumber: string;
  status: ReceivingLotStatus;
  customerId: string;
  customerName: string;
  customerLocationId?: string | null;
  customerLocationName?: string | null;
  receivedAt: string;
  equipmentCount: number;
  modelBatchCount: number;
  modelBatchPreview: ModelBatchSummary[];
  modelBatchPreviewRemaining: number;
  arrivalCount: number;
  latestArrivalAt: string;
  createdAt: string;
}

export interface ReceivingLotListResponse {
  data: ReceivingLotListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type ReceivingLotSortField =
  'receivedAt' | 'lotNumber' | 'customerName' | 'equipmentCount' | 'createdAt';

export interface ReceivingLotListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  customerId?: string;
  customerLocationId?: string;
  equipmentModelId?: string;
  status?: ReceivingLotStatus;
  receivedFrom?: string;
  receivedTo?: string;
  sortBy?: ReceivingLotSortField;
  sortDir?: 'asc' | 'desc';
}

export interface ModelBatchEquipmentItem {
  id: string;
  equipmentName: string;
  assetNumber: string;
  serialNumber: string;
  status: string;
  receivedIntoLotAt: string;
}

export interface ModelBatchEquipmentListResponse {
  receivingLotId: string;
  modelBatchId: string;
  equipmentModelId: string;
  equipmentModelName?: string | null;
  data: ModelBatchEquipmentItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface Arrival {
  id: string;
  receivingLotId: string;
  arrivalSequence: number;
  status: string;
  sourceType: string;
  receivedAt: string;
  receivedByUserId: string;
  courierName?: string | null;
  trackingNumber?: string | null;
  packageCount?: number | null;
  equipmentCount: number;
  remarks?: string | null;
  createdAt: string;
}

export interface ArrivalListResponse {
  data: Arrival[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AddArrivalParams {
  equipmentIds: string[];
  receivedAt?: string;
  courierName?: string;
  trackingNumber?: string;
  packageCount?: number;
  remarks?: string;
  idempotencyKey: string;
}

const base = '/receiving-lots';

export const receivingLotService = {
  list: (params: ReceivingLotListParams): Promise<ReceivingLotListResponse> =>
    apiClient.get<ReceivingLotListResponse>(base, { params }).then((r) => r.data),

  get: (id: string): Promise<ReceivingLot> =>
    apiClient.get<ReceivingLot>(`${base}/${id}`).then((r) => r.data),

  listBatchEquipment: (
    lotId: string,
    batchId: string,
    params: { page?: number; pageSize?: number; search?: string },
  ): Promise<ModelBatchEquipmentListResponse> =>
    apiClient
      .get<ModelBatchEquipmentListResponse>(`${base}/${lotId}/model-batches/${batchId}/equipment`, {
        params,
      })
      .then((r) => r.data),

  addArrival: (lotId: string, params: AddArrivalParams): Promise<Arrival> =>
    apiClient.post<Arrival>(`${base}/${lotId}/arrivals`, params).then((r) => r.data),

  listArrivals: (
    lotId: string,
    params: { page?: number; pageSize?: number },
  ): Promise<ArrivalListResponse> =>
    apiClient.get<ArrivalListResponse>(`${base}/${lotId}/arrivals`, { params }).then((r) => r.data),

  getArrival: (lotId: string, arrivalId: string): Promise<Arrival> =>
    apiClient.get<Arrival>(`${base}/${lotId}/arrivals/${arrivalId}`).then((r) => r.data),

  markReceivingComplete: (lotId: string): Promise<ReceivingLot> =>
    apiClient.post<ReceivingLot>(`${base}/${lotId}/receiving-complete`).then((r) => r.data),

  close: (lotId: string): Promise<ReceivingLot> =>
    apiClient.post<ReceivingLot>(`${base}/${lotId}/close`).then((r) => r.data),

  reopen: (lotId: string, reason: string): Promise<ReceivingLot> =>
    apiClient.post<ReceivingLot>(`${base}/${lotId}/reopen`, { reason }).then((r) => r.data),
};
